import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Grid2,
  MenuItem,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Header from "../../Header";
import Footer from "../../Footer";
import Grid from "@mui/material/Grid2";
import { Iconify } from "../../Minimal/src/components/iconify/iconify";
import { getProformaInvoice } from "../../../data/invoice";
import printNewSalesInvoice from "../../nsales/printNewSalesInvoice";
import printNewSalesInvoice_tsc from "../../nsales/printNewSalesInvoice_tsc";
import printNewSalesInvoice_knm from "../../nsales/printNewSalesInvoice_knm";
import printNewSalesInvoice_sdm from "../../nsales/printNewSalesInvoice_sdm";
import printNewSalesInvoice_rhv from "../../nsales/printNewSalesInvoice_rhv";
import printNewSalesInvoice_eve from "../../nsales/generateSummaryPDF_eve";
import generateSummaryPDF_atr from "../../nsales/generateSummaryPDF_atr";
import config from "../../common/Config";
import { getRhpInvoicePrintUrl, shouldUseRhpInvoicePrint } from "../Epos/utils/rhpInvoicePrint";
import "../../common/commonStyles.css";

const formatType = (value) => {
  if (!value) return "Box";
  const stringValue = String(value);
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
};

const InvoiceView = () => {
  const accessToken = localStorage.getItem("accessToken");
  const { id } = useParams();
  const [invoiceFormatType, setInvoiceFormatType] = useState("1");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["proformaInvoice", id],
    queryFn: () => getProformaInvoice(id),
    enabled: !!id,
  });

  const transformData = () => {
    if (!data?.details?.orderDetails?.[0]) return null;

    const order = data.details.orderDetails[0];
    const products = data.details.product_details || [];
    const bundleDiscounts = data.details.bundle_discounts || [];

    const calculateTotals = () => {
      let totalStdGoods = 0;
      let vatAmount = 0;
      let totalZeroGoods = 0;

      products.forEach((product) => {
        const subtotal = parseFloat(product.subtotal || 0);
        const tax = parseFloat(product.item_tax || 0);
        const taxPercent = parseFloat(product.tax_rate_percent || 0);

        vatAmount += tax;

        if (taxPercent === 0) {
          totalZeroGoods += subtotal;
        } else {
          totalStdGoods += subtotal;
        }
      });

      // Calculate promo discount: sum of promo_disc_amount from all products
      // If promo_disc_amount is not available, fall back to (original_price - discount_price) * quantity
      const promotionalDiscount = products.reduce((sum, product) => {
        const promoDiscAmount = parseFloat(product.promo_disc_amount || 0);
        if (promoDiscAmount > 0) {
          return sum + promoDiscAmount;
        }
        // Fallback calculation if promo_disc_amount is not populated
        const originalPrice = parseFloat(
          product.original_price || product.unit_price || 0,
        );
        const discountPrice = parseFloat(
          product.discount_price || product.unit_price || 0,
        );
        const quantity = parseFloat(product.quantity || 0);
        return sum + (originalPrice - discountPrice) * quantity;
      }, 0);

      const calculatedInvoiceTotal =
        parseFloat(order.grand_total || 0) > 0
          ? parseFloat(order.grand_total)
          : totalStdGoods + vatAmount + totalZeroGoods;
      const totalInvDiscPercent = parseFloat(order.over_all_discount || 0);
      const totalInvDiscAmount = parseFloat(order.over_all_amount || 0);
      const totalPromoDiscountFromBundle = bundleDiscounts.reduce(
        (sum, item) => sum + parseFloat(item.total_promo_discount || 0),
        0,
      );
      const totalPromoDiscountVatFromBundle = bundleDiscounts.reduce(
        (sum, item) => sum + parseFloat(item.total_promo_discount_vat || 0),
        0,
      );
      const totalPromoDiscount =
        totalPromoDiscountFromBundle > 0
          ? totalPromoDiscountFromBundle
          : parseFloat(order.total_promo_discount || 0);
      const totalPromoDiscountVat =
        totalPromoDiscountVatFromBundle > 0
          ? totalPromoDiscountVatFromBundle
          : parseFloat(order.total_promo_discount_vat || 0);

      return {
        totalStdGoods,
        vatAmount,
        totalZeroGoods,
        total_promo_discount: totalPromoDiscount,
        total_promo_discount_vat: totalPromoDiscountVat,
        promotionalDiscount,
        totalInvDisc: totalInvDiscPercent,
        totalInvDiscAmount,
        orderDiscount: parseFloat(order.order_discount || 0),
        invoiceTotal: calculatedInvoiceTotal,
      };

    };

    return {
      customerName: order.name?.trim() || "",
      address:
        `${order.address || ""}, ${order.city || ""}, ${order.postal_code || ""}`.replace(
          /^,\s*|,\s*$/g,
          "",
        ),
      accountNo: order.account_no || "",
      tel: order.phone || "",
      email: order.email || "",
      orderDate: `${order.date || ""} ${order.time || ""}`,
      orderNumber: order.reference_no || "",
      ownOrderNumber: String(order.own_order_number || "").trim(),
      saleStatus: order.sale_status || "",
      routeNumber: order.route_number || "",
      deliveryDate: order.deliverydate || order.delivery_date || "",
      paymentStatus: order.payment_status || "Due",
      staffNote: (order.staff_note || "").trim(),
      products: products.map((product) => {
        const originalPrice = parseFloat(
          product.original_price || product.unit_price || 0,
        );
        const discountPrice = parseFloat(
          product.discount_price || product.unit_price || 0,
        );
        const subtotal = parseFloat(product.subtotal || 0);
        const hasPromotion =
          product.is_promoted === "1" ||
          product.is_promoted === 1 ||
          Number(product.promo_id) > 0 ||
          Number(product.promoId) > 0 ||
          Number(product.promosId) > 0 ||
          Number(product.promotion_id) > 0 ||
          Number(product.promotionId) > 0;
        const isFree =
          hasPromotion &&
          (product.free_product === "1" ||
            product.free_product === 1 ||
            product.free_product === "(F)" ||
            product.free_product === "Y" ||
            originalPrice === 0 ||
            Math.abs(subtotal) < 0.000001);
        const isDiscounted = hasPromotion && !isFree;

        const rawDiscountType =
          product.productWiseDiscountType || product.discount_type || "amount";
        const discountType =
          String(rawDiscountType).toLowerCase() === "percentage"
            ? "percentage"
            : "amount";
        const promotionName = String(
          product.promotion_name || product.promo_name || "",
        ).trim();
        const promotionDiscount =
          parseFloat(
            product.promotion_discount ??
              product.promo_disc_amount ??
              product.promo_discount_amount ??
              product.promo_discount ??
              0,
          ) || 0;
        const rawItemDiscount =
          discountType === "percentage"
            ? parseFloat(product.discount || product.discount_percentage || 0)
            : parseFloat(product.item_discount || 0);
        const discount =
          isDiscounted && promotionDiscount > 0.0001 ? 0 : rawItemDiscount;

        return {
          id: product.id,
          description: `${product.product_name} | ${product.size}`,
          qty: parseFloat(product.quantity || 0),
          type: formatType(product.order_type || "box"),
          unitPrice: parseFloat(product.unit_price || 0),
          originalPrice: originalPrice,
          discount,
          discountType,
          vatAmount: parseFloat(product.item_tax || 0),
          netAmount: parseFloat(product.subtotal || 0),
          isFree,
          isDiscounted,
          promotionName,
          promotionDiscount,
        };
      }),
      totals: calculateTotals(),
    };
  };

  const invoiceData = transformData();

  const labelText = {
    fontWeight: 500,
    color: "#212b36",
    fontSize: "0.87rem",
    variant: "body2",
  };

  const valueText = {
    color: "#212b36",
    fontSize: "0.87rem",
    minWidth: 80,
    textAlign: "right",
    variant: "body2",
  };

  const summaryRow = {
    display: "flex",
    gap: 1.5,
    justifyContent: "flex-end",
    alignItems: "center",
  };

  const textLabel = {
    fontWeight: 500,
    color: "#212b36",
    fontSize: "0.87rem",
  };
  const textValue = {
    color: "#212b36",
    fontSize: "0.8rem",
  };

  if (isLoading) {
    return (
      <div>
        <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", my: 6 }}>
          <Box sx={{ width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(145, 158, 171, 0.16)",
                overflow: "hidden",
                backgroundColor: "#fff",
                p: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <CircularProgress />
            </Paper>
          </Box>
        </Box>
      </div>
    );
  }

  if (!invoiceData || isError) {
    return (
      <div>
        <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", my: 6 }}>
          <Box sx={{ width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(145, 158, 171, 0.16)",
                overflow: "hidden",
                backgroundColor: "#fff",
                p: 3,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                gap: 2,
              }}
            >
              <Iconify
                icon="solar:clipboard-remove-bold"
                width={120}
                sx={{ color: "#637381" }}
              />
              <Typography
                variant="h5"
                sx={{ color: "#212b36", fontWeight: 600 }}
              >
                Invoice Not found
              </Typography>
            </Paper>
          </Box>
        </Box>
      </div>
    );
  }

  const formatCurrency = (value) => {
    const currencySymbol = localStorage.getItem("currencySymbol") || "£";
    return `${currencySymbol}${parseFloat(value || 0).toFixed(2)}`;
  };

  const handleGenerateDeliveryNotePDF = (e, orderId) => {
    const accessToken = localStorage.getItem("accessToken");

    // const printformatType = invoiceFormatType;
    // // console.log(accessToken);
    // // return;
    // if (printformatType || accessToken === "tsc" || accessToken === "tss") {
    //   printNewSalesInvoice_tsc(orderId);
    // } else if (printformatType || accessToken === "eve") {
    //   printNewSalesInvoice_eve(orderId);
    // } else if (printformatType || accessToken === "knm") {
    //   printNewSalesInvoice_knm(orderId);
    // } else if (printformatType || accessToken === "sdm") {
    //   printNewSalesInvoice_sdm(orderId);
    // } else if (printformatType || accessToken === "rhv" || accessToken === "hv") {
    //   printNewSalesInvoice_rhv(orderId);
    // } else if (printformatType || accessToken === "rhp" || accessToken === "hp") {
    //   window.open(
    //     `/invoice/right-price-wholsale.html?orderId=${orderId}`,
    //     "_blank",
    //   );
    // } else {

    //   printNewSalesInvoice(orderId);
    // }

    const formatMap = {
      "1": "ebu",
      "2": "eve",
      "3": "tsc",
      "4": "rhp",
      "5": "knm",
    };
    const selectedFormatKey =
      accessToken === "ebu" || accessToken === "bu" ? invoiceFormatType : "";
    const selectedFormat = selectedFormatKey ? (formatMap[selectedFormatKey] || selectedFormatKey) : "";

    const printByFormat = (formatKey) => {
      if (formatKey === "tsc" || formatKey === "tss") {
        printNewSalesInvoice_tsc(orderId);
      } else if (formatKey === "eve") {
        printNewSalesInvoice_eve(orderId);
      } else if (formatKey === "knm") {
        printNewSalesInvoice_knm(orderId);
      } else if (formatKey === "sdm") {
        printNewSalesInvoice_sdm(orderId);
      } else if (formatKey === "rhv" || formatKey === "hv") {
        printNewSalesInvoice_rhv(orderId);
      } else if (formatKey === "rhp" || formatKey === "hp") {
        if (shouldUseRhpInvoicePrint()) {
          window.open(
            getRhpInvoicePrintUrl(orderId, { autoprint: true }),
            "_blank",
            "noopener,noreferrer",
          );
        } else {
          printNewSalesInvoice(orderId);
        }
      } else if (formatKey === "atr") {
        generateSummaryPDF_atr(orderId);
      } else {
        printNewSalesInvoice(orderId);
      }
    };

    if (selectedFormat) {
      printByFormat(selectedFormat);
      return;
    }

    printByFormat(accessToken);
  };


  return (
    <>
      <Box sx={{ width: "100%", p: 1.5, backgroundColor: "#f5f7fa" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(145, 158, 171, 0.16)",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#212b36",
                    fontSize: "1.25rem",
                  }}
                >
                  New Sales Invoice
                </Typography>
              </Grid2>
              <Grid2
                size={{ xs: 12, md: 6 }}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
              >
                {(accessToken === "ebu" || accessToken === "bu") && (
                  <TextField
                    label="Select Format Type"
                    value={invoiceFormatType}
                    onChange={(e) => setInvoiceFormatType(e.target.value)}
                    select
                    sx={{ width: 180 }}
                    slotProps={{
                      input: {
                        sx: {
                          height: "36px",
                        },
                      },
                    }}
                  >
                    <MenuItem value="1">Format 1</MenuItem>
                    <MenuItem value="2">Format 2</MenuItem>
                    <MenuItem value="3">Format 3</MenuItem>
                    <MenuItem value="4">Format 4</MenuItem>
                    <MenuItem value="5">Format 5</MenuItem>
                  </TextField>
                )
                }
                <Button
                  variant="contained"
                  size="small"
                  className="button-variant-contained"
                  startIcon={<Iconify icon="solar:printer-bold" width={16} />}
                  onClick={(e) => handleGenerateDeliveryNotePDF(e, id)}
                  
                >
                  Print Invoice
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <Box>
            <Grid2 container spacing={2} sx={{ px: 2, py: 1 }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Customer Name:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.customerName}
                    </Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Address:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.address}
                    </Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Customer a/c No.</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.accountNo}
                    </Typography>
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Box>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Tel:</Typography>
                    <Typography sx={textValue}>{invoiceData.tel}</Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Email:</Typography>
                    <Typography sx={textValue}>{invoiceData.email}</Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Order Date:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.orderDate}
                    </Typography>
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Box>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Order:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.orderNumber}
                    </Typography>
                  </Typography>
                  {(accessToken === "atr" || config.PREFIX === "atr") && invoiceData.ownOrderNumber ? (
                    <Typography display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={textLabel}>Your own order number:</Typography>
                      <Typography sx={textValue}>
                        {invoiceData.ownOrderNumber}
                      </Typography>
                    </Typography>
                  ) : null}
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Sale Status:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.saleStatus}
                    </Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>
                      {accessToken === "atr" ? "Delivery Date:" : "Route Number:"}
                    </Typography>
                    <Typography sx={textValue}>
                      {accessToken === "atr"
                        ? invoiceData.deliveryDate
                        : invoiceData.routeNumber}
                    </Typography>
                  </Typography>
                  <Typography display="flex" alignItems="center" gap={0.5}>
                    <Typography sx={textLabel}>Payment Status:</Typography>
                    <Typography sx={textValue}>
                      {invoiceData.paymentStatus}
                    </Typography>
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>

            {invoiceData.staffNote ? (
              <Box sx={{ mb: 2 }}>
                <Typography display="flex" alignItems="flex-start" gap={0.5}>
                  <Typography sx={textLabel}>Note:</Typography>
                  <Typography sx={{ ...textValue, whiteSpace: "pre-wrap" }}>
                    {invoiceData.staffNote}
                  </Typography>
                </Typography>
              </Box>
            ) : null}

            <TableContainer sx={{ mb: 2, overflowX: "auto" }}>
              <Table className="table-cell-border" size="small">
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell sx={{ width: "30%" }}>
                      Product Description
                    </TableCell>
                    <TableCell align="center" sx={{ width: "8%" }}>
                      Qty
                    </TableCell>
                    <TableCell align="center" sx={{ width: "10%" }}>
                      Type
                    </TableCell>
                    <TableCell align="right" sx={{ width: "12%" }}>
                      Unit Price
                    </TableCell>
                    <TableCell align="right" sx={{ width: "10%" }}>
                      Discount
                    </TableCell>
                    <TableCell align="right" sx={{ width: "10%" }}>
                      VAT Amount
                    </TableCell>
                    <TableCell align="right" sx={{ width: "10%" }}>
                      Net Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceData.products.map((product) => {
                    const showAtrPromoRow =
                      (accessToken === "atr" || config.PREFIX === "atr") &&
                      product.isDiscounted &&
                      product.promotionName &&
                      product.promotionDiscount > 0.0001;
                    return (
                      <React.Fragment key={product.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)",
                        },
                        transition: "background-color 0.2s ease",
                        "& .MuiTableCell-root": {
                          fontSize: "0.75rem",
                          color: "#212b36",
                          py: 1,
                        },
                      }}
                    >
                      <TableCell
                        sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            color: "#212b36",
                            display: "block",
                          }}
                        >
                          {product.description}
                        </Typography>
                        {product.isFree && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Iconify
                              icon="solar:circle-bold"
                              width={4}
                              sx={{ color: "#1976d2", mr: 0.25 }}
                            />
                            <Typography
                              variant="body2"
                              display="flex"
                              alignItems="center"
                              gap={0.25}
                              sx={{ color: "#1976d2", fontSize: "0.65rem" }}
                            >
                              <Iconify
                                icon="solar:check-circle-bold"
                                width={10}
                              />
                              (F)
                            </Typography>
                          </Box>
                        )}
                        {product.isDiscounted && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 0.25,
                            }}
                          >
                            <Iconify
                              icon="solar:circle-bold"
                              width={4}
                              sx={{ color: "#1976d2", mr: 0.25 }}
                            />
                            <Typography
                              variant="body2"
                              display="flex"
                              alignItems="center"
                              gap={0.25}
                              sx={{ color: "#1976d2", fontSize: "0.65rem" }}
                            >
                              <Iconify
                                icon="solar:check-circle-bold"
                                width={10}
                              />
                              (P)
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#212b36" }}
                        >
                          {product.qty}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#212b36" }}
                        >
                          {product.type}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}
                      >
                        {product.isDiscounted ? (
                          <>
                            {product.originalPrice !== product.unitPrice && Math.abs(product.originalPrice - product.unitPrice) > 0.0001 && (
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "#637381",
                                  display: "block",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {formatCurrency(product.originalPrice)}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                display: "block",
                                fontSize: "0.75rem",
                                color: "#212b36",
                              }}
                            >
                              {formatCurrency(product.unitPrice)}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.75rem", color: "#212b36" }}
                          >
                            {formatCurrency(product.unitPrice)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}
                      >
                    
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem", color: "#212b36" }}
                            >
                              {product.discountType === "percentage"
                                ? `${product.discount}%`
                                : formatCurrency(product.discount)}
                            </Typography>
                          </Box>
             
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "#212b36" }}
                        >
                     {formatCurrency(product.vatAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            color: "#212b36",
                            fontWeight: 600,
                          }}
                        >
                       {formatCurrency(product.netAmount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {showAtrPromoRow ? (
                      <TableRow
                        sx={{
                          "& .MuiTableCell-root": {
                            fontSize: "0.75rem",
                            color: "#212b36",
                            py: 0.5,
                            borderTop: 0,
                          },
                        }}
                      >
                        <TableCell colSpan={6}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "#212b36",
                            }}
                          >
                            {product.promotionName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "#212b36",
                            }}
                          >
                            -{formatCurrency(product.promotionDiscount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} />
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    px: 2,
                  }}
                >
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>VAT Goods</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.totalStdGoods)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>VAT Amount</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.vatAmount)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>Zero VAT Goods</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.totalZeroGoods)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>Promo Discount</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.total_promo_discount)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>Promo Discount VAT</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.total_promo_discount_vat)}
                    </Typography>
                  </Box>
                  {/* <Box sx={summaryRow}>
                    <Typography sx={labelText}>Promotional Discount</Typography>
                    <Typography sx={valueText}>
                      {formatCurrency(invoiceData.totals.promotionalDiscount)}
                    </Typography>
                  </Box> */}
                  <Box sx={summaryRow}>
                    <Typography sx={labelText}>Order Discount</Typography>
                    <Typography sx={valueText}>
                      {invoiceData.totals.totalInvDisc > 0
                        ? `${invoiceData.totals.totalInvDisc.toFixed(2)}% (${formatCurrency(invoiceData.totals.totalInvDiscAmount)})`
                        : `${invoiceData.totals.totalInvDisc.toFixed(2)}%`}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ my: 0.5, borderColor: "rgba(145, 158, 171, 0.16)" }}
                  />
                  <Box sx={summaryRow}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#212b36",
                        fontSize: "0.875rem",
                      }}
                    >
                      Invoice Total
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#212b36",
                        fontSize: "0.875rem",
                        minWidth: 80,
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(invoiceData.totals.invoiceTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 2.5,
                py: 2,
                borderTop: "1px solid rgba(145, 158, 171, 0.16)",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#637381", fontSize: "0.875rem" }}
              >
                No claims for damages or short delivery will be considered
                unless made at the time of delivery of goods. These goods are
                not sold on a sale or return basis.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default InvoiceView;
